import React, { useState, useEffect, useContext } from 'react';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import './Modal.css';

const UserFormModal = ({ isOpen, user, onClose, onSubmit }) => {
  const { user: currentUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    profile_id: '',
    transito_id: '',
    concesionario_id: '',
  });
  const [profiles, setProfiles] = useState([]);
  const [transitos, setTransitos] = useState([]);
  const [concesionarios, setConcesionarios] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchProfiles();
      fetchTransitos();
      fetchConcesionarios();
    }
  }, [isOpen]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get('/api/profiles');
      setProfiles(response.data);
    } catch (err) {
      console.error('Error fetching profiles:', err);
    }
  };

  const fetchTransitos = async () => {
    try {
      const response = await axios.get('/api/transitos');
      setTransitos(response.data);
    } catch (err) {
      console.error('Error fetching transitos:', err);
    }
  };

  const fetchConcesionarios = async () => {
    try {
      const response = await axios.get('/api/concesionarios');
      setConcesionarios(response.data);
    } catch (err) {
      console.error('Error fetching concesionarios:', err);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role: user.role,
        profile_id: user.profile_id || '',
        transito_id: user.transito_id || '',
        concesionario_id: user.concesionario_id || '',
      });
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'user',
        profile_id: '',
        transito_id: '',
        concesionario_id: '',
      });
    }
    setError('');
  }, [user, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username || !formData.email || !formData.role) {
      setError('Username, email y rol son requeridos');
      return;
    }

    if (!user && !formData.password) {
      setError('La contraseña es requerida para nuevos usuarios');
      return;
    }

    setLoading(true);

    const data = {
      username: formData.username,
      email: formData.email,
      role: formData.role,
      profile_id: formData.profile_id || null,
      transito_id: formData.transito_id || null,
      concesionario_id: formData.concesionario_id || null,
    };

    if (formData.password) {
      data.password = formData.password;
    }

    try {
      await onSubmit(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => !loading && onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{user ? '✏️ Editar Usuario' : '➕ Nuevo Usuario'}</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="username">Usuario:</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Nombre de usuario"
              disabled={loading || !!user}
              className={user ? 'disabled-input' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="correo@ejemplo.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Contraseña: {user && '(Dejar en blanco para no cambiar)'}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={user ? 'Dejar en blanco para mantener actual' : 'Contraseña'}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="role">Rol:</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="user">👤 Usuario</option>
              {currentUser?.role === 'superadmin' && <option value="admin">👨‍💼 Admin</option>}
              {currentUser?.role === 'superadmin' && <option value="superadmin">🔐 Superadmin</option>}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="profile_id">Perfil (Módulos):</label>
            <select
              id="profile_id"
              name="profile_id"
              value={formData.profile_id}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Ninguno (Sin módulos específicos)</option>
              {profiles
                .filter(p => currentUser?.role === 'superadmin' || p.name !== 'superadmin')
                .map(p => (
                  <option key={p.id} value={p.id}>🛡️ {p.name}</option>
                ))}
            </select>
          </div>

          {/* Mostrar Tránsito solo si no es admin de concesionario */}
          {!currentUser?.concesionario_id && (
            <div className="form-group">
              <label htmlFor="transito_id">Empresa (Tránsito):</label>
              <select
                id="transito_id"
                name="transito_id"
                value={formData.transito_id}
                onChange={(e) => {
                  handleChange(e);
                  if (e.target.value) {
                    setFormData(prev => ({ ...prev, concesionario_id: '' }));
                  }
                }}
                disabled={loading || (formData.concesionario_id !== '') || !!currentUser?.transito_id}
              >
                <option value="">Ninguna (Global o Concesionario)</option>
                {transitos.map(t => (
                  <option key={t.id} value={t.id}>🏢 {t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Mostrar Concesionario solo si no es admin de tránsito */}
          {!currentUser?.transito_id && (
            <div className="form-group">
              <label htmlFor="concesionario_id">Concesionario (Tramitador):</label>
              <select
                id="concesionario_id"
                name="concesionario_id"
                value={formData.concesionario_id}
                onChange={(e) => {
                  handleChange(e);
                  if (e.target.value) {
                    setFormData(prev => ({ ...prev, transito_id: '' }));
                  }
                }}
                disabled={loading || (formData.transito_id !== '') || !!currentUser?.concesionario_id}
              >
                <option value="">Ninguno (Global o Tránsito)</option>
                {concesionarios.map(c => (
                  <option key={c.id} value={c.id}>🏪 {c.name}</option>
                ))}
              </select>
              {formData.transito_id && <small style={{color: '#64748b'}}>Un usuario no puede pertenecer a un Tránsito y un Concesionario a la vez.</small>}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-save"
              disabled={loading}
            >
              {loading ? '⏳ Guardando...' : user ? '✓ Actualizar' : '✓ Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
